import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Stepper,
  Step,
  StepLabel,
  FormControlLabel,
  Checkbox,
  Chip,
  InputAdornment,
  IconButton,
  Divider,
  StepConnector,
  stepConnectorClasses
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { db } from '../config/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

// Default user ID for demo purposes
const DEFAULT_USER_ID = "user123";

// Add enhanced input styles for better contrast and consistency
const enhancedInputStyles = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: '#f8f8f8',
    '&:hover': {
      backgroundColor: '#f0f0f0',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#c0c0c0',
    },
    '&.Mui-focused': {
      boxShadow: '0 0 0 3px rgba(255, 215, 0, 0.2)',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#FFD700',
        borderWidth: '1px',
      },
    },
  },
  '& .MuiInputLabel-root': {
    color: '#555',
    fontWeight: 500,
    '&.Mui-focused': {
      color: '#333',
    },
  },
  '& .MuiInputBase-input': {
    color: '#333',
    fontSize: '1rem',
    padding: '12px 14px',
  },
};

// Define a style for the textarea
const textareaStyles = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: '#f8f8f8',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#c0c0c0',
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#a0a0a0',
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#FFD700',
    borderWidth: '1px',
    boxShadow: '0 0 0 3px rgba(255, 215, 0, 0.2)',
  },
};

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

// Style for custom color stepper connector
const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 22,
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage: 'linear-gradient( 95deg, #FFD700 0%, #FFED8A 50%, #FFD700 100%)',
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage: 'linear-gradient( 95deg, #FFD700 0%, #FFED8A 50%, #FFD700 100%)',
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 3,
    border: 0,
    backgroundColor: '#eaeaf0',
    borderRadius: 1,
  },
}));

const ProviderApplication = () => {
  const navigate = useNavigate();
  
  const steps = [
    'Información Personal',
    'Detalles Profesionales',
    'Verificación y Términos'
  ];
  
  const [activeStep, setActiveStep] = useState(0);
  const [bioFocused, setBioFocused] = useState(false);
  const [bioHovered, setBioHovered] = useState(false);
  const [ratesFocused, setRatesFocused] = useState(false);
  const [ratesHovered, setRatesHovered] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    profession: '',
    experience: '',
    bio: '',
    services: [],
    newService: '',
    skills: [],
    newSkill: '',
    certifications: [],
    newCertification: '',
    serviceRates: '',
    serviceAreas: '',
    workRadius: '',
    termsAccepted: false
  });
  
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  
  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    
    if (name === 'termsAccepted') {
      setFormData({
        ...formData,
        [name]: checked
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };
  
  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };
  
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };
  
  const handleAddService = () => {
    if (formData.newService && !formData.services.includes(formData.newService)) {
      setFormData({
        ...formData,
        services: [...formData.services, formData.newService],
        newService: ''
      });
    }
  };
  
  const handleRemoveService = (service) => {
    setFormData({
      ...formData,
      services: formData.services.filter(s => s !== service)
    });
  };
  
  const handleAddSkill = () => {
    if (formData.newSkill && !formData.skills.includes(formData.newSkill)) {
      setFormData({
        ...formData,
        skills: [...formData.skills, formData.newSkill],
        newSkill: ''
      });
    }
  };
  
  const handleRemoveSkill = (skill) => {
    setFormData({
      ...formData,
      skills: formData.skills.filter(s => s !== skill)
    });
  };
  
  const handleAddCertification = () => {
    if (formData.newCertification && !formData.certifications.includes(formData.newCertification)) {
      setFormData({
        ...formData,
        certifications: [...formData.certifications, formData.newCertification],
        newCertification: ''
      });
    }
  };
  
  const handleRemoveCertification = (certification) => {
    setFormData({
      ...formData,
      certifications: formData.certifications.filter(c => c !== certification)
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.termsAccepted) {
      setErrorMessage('Debes aceptar los términos y condiciones para continuar.');
      return;
    }
    
    try {
      // Create provider application in Firestore
      await addDoc(collection(db, 'providerApplications'), {
        ...formData,
        userId: DEFAULT_USER_ID,
        status: 'pending',
        createdAt: serverTimestamp()
      });
      
      setSuccessMessage('¡Tu solicitud ha sido enviada con éxito! Te contactaremos pronto.');
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (error) {
      console.error('Error submitting provider application:', error);
      setErrorMessage('Hubo un error al enviar tu solicitud. Por favor intenta de nuevo.');
    }
  };
  
  const renderPersonalInfoStep = () => {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500, color: '#333' }}>
            Nombre Completo *
          </Typography>
          <TextField
            fullWidth
            name="fullName"
            value={formData.fullName}
            onChange={handleChange}
            required
            variant="outlined"
            placeholder="Ingresa tu nombre completo"
            sx={enhancedInputStyles}
          />
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500, color: '#333' }}>
            Teléfono *
          </Typography>
          <TextField
            fullWidth
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            required
            variant="outlined"
            placeholder="Ej: 55 1234 5678"
            sx={enhancedInputStyles}
          />
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500, color: '#333' }}>
            Correo Electrónico *
          </Typography>
          <TextField
            fullWidth
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
            variant="outlined"
            placeholder="tu@correo.com"
            sx={enhancedInputStyles}
          />
        </Grid>
      </Grid>
    );
  };
  
  const renderProfessionalDetailsStep = () => {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500, color: '#333' }}>
            Profesión *
          </Typography>
          <TextField
            fullWidth
            name="profession"
            value={formData.profession}
            onChange={handleChange}
            required
            variant="outlined"
            placeholder="Ej: Plomero, Electricista, Carpintero"
            sx={enhancedInputStyles}
          />
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500, color: '#333' }}>
            Años de Experiencia *
          </Typography>
          <TextField
            fullWidth
            name="experience"
            value={formData.experience}
            onChange={handleChange}
            required
            variant="outlined"
            placeholder="Ej: 5"
            type="number"
            sx={enhancedInputStyles}
          />
        </Grid>
        
        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500, color: '#333' }}>
            Biografía *
          </Typography>
          <Box sx={{ width: '100%' }}>
            <textarea
              style={{
                backgroundColor: bioHovered && !bioFocused ? '#f9f9f9' : '#ffffff',
                borderRadius: '8px',
                border: bioFocused ? '2px solid #FFD700' : bioHovered ? '1px solid #a0a0a0' : '1px solid #c0c0c0',
                minHeight: '120px',
                padding: bioFocused ? '13px 15px' : '14px 16px',
                width: '100%',
                fontFamily: 'inherit',
                fontSize: '1rem',
                lineHeight: '1.5',
                color: '#333',
                resize: 'vertical',
                outline: 'none',
                boxShadow: bioFocused ? '0 0 0 3px rgba(255, 215, 0, 0.2)' : 'none',
                transition: 'border 0.2s, box-shadow 0.2s, background-color 0.2s'
              }}
              placeholder="Cuéntanos sobre ti y tu experiencia profesional..."
              name="bio"
              value={formData.bio}
              onChange={handleChange}
              onFocus={() => setBioFocused(true)}
              onBlur={() => setBioFocused(false)}
              onMouseEnter={() => setBioHovered(true)}
              onMouseLeave={() => setBioHovered(false)}
              required
            />
          </Box>
          {formData.bio === '' && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1, ml: 1 }}>
              Este campo es requerido
            </Typography>
          )}
        </Grid>
        
        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
            Servicios Ofrecidos
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {formData.services.map((service, index) => (
              <Chip
                key={index}
                label={service}
                onDelete={() => handleRemoveService(service)}
                sx={{
                  backgroundColor: '#FFE500',
                  '&:hover': { backgroundColor: '#FFD700' },
                  color: 'rgba(0,0,0,0.7)',
                  fontWeight: 500,
                }}
              />
            ))}
          </Box>
          
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500, color: '#333' }}>
            Agregar Servicio
          </Typography>
          <TextField
            fullWidth
            name="newService"
            value={formData.newService}
            onChange={handleChange}
            variant="outlined"
            placeholder="Ej: Instalación de lámparas"
            sx={enhancedInputStyles}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleAddService} edge="end">
                    <AddIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        
        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500, color: '#333' }}>
            Tarifas de Servicios *
          </Typography>
          <Box sx={{ width: '100%' }}>
            <textarea
              style={{
                backgroundColor: ratesHovered && !ratesFocused ? '#f9f9f9' : '#ffffff',
                borderRadius: '8px',
                border: ratesFocused ? '2px solid #FFD700' : ratesHovered ? '1px solid #a0a0a0' : '1px solid #c0c0c0',
                minHeight: '80px',
                padding: ratesFocused ? '13px 15px' : '14px 16px',
                width: '100%',
                fontFamily: 'inherit',
                fontSize: '1rem',
                lineHeight: '1.5',
                color: '#333',
                resize: 'vertical',
                outline: 'none',
                boxShadow: ratesFocused ? '0 0 0 3px rgba(255, 215, 0, 0.2)' : 'none',
                transition: 'border 0.2s, box-shadow 0.2s, background-color 0.2s'
              }}
              placeholder="Detalla tus tarifas por tipo de servicio..."
              name="serviceRates"
              value={formData.serviceRates}
              onChange={handleChange}
              onFocus={() => setRatesFocused(true)}
              onBlur={() => setRatesFocused(false)}
              onMouseEnter={() => setRatesHovered(true)}
              onMouseLeave={() => setRatesHovered(false)}
              required
            />
          </Box>
          {formData.serviceRates === '' && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1, ml: 1 }}>
              Este campo es requerido
            </Typography>
          )}
        </Grid>
        
        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
            Habilidades
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {formData.skills.map((skill, index) => (
              <Chip
                key={index}
                label={skill}
                onDelete={() => handleRemoveSkill(skill)}
                sx={{
                  backgroundColor: '#f0f0f0',
                  '&:hover': { backgroundColor: '#e0e0e0' },
                  color: 'rgba(0,0,0,0.7)',
                  fontWeight: 500,
                }}
              />
            ))}
          </Box>
          
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500, color: '#333' }}>
            Agregar Habilidad
          </Typography>
          <TextField
            fullWidth
            name="newSkill"
            value={formData.newSkill}
            onChange={handleChange}
            variant="outlined"
            placeholder="Ej: Lectura de planos"
            sx={enhancedInputStyles}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleAddSkill} edge="end">
                    <AddIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        
        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
            Certificaciones
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {formData.certifications.map((certification, index) => (
              <Chip
                key={index}
                label={certification}
                onDelete={() => handleRemoveCertification(certification)}
                sx={{
                  backgroundColor: '#f0f0f0',
                  '&:hover': { backgroundColor: '#e0e0e0' },
                  color: 'rgba(0,0,0,0.7)',
                  fontWeight: 500,
                }}
              />
            ))}
          </Box>
          
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500, color: '#333' }}>
            Agregar Certificación
          </Typography>
          <TextField
            fullWidth
            name="newCertification"
            value={formData.newCertification}
            onChange={handleChange}
            variant="outlined"
            placeholder="Ej: Certificado en instalaciones eléctricas"
            sx={enhancedInputStyles}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleAddCertification} edge="end">
                    <AddIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>
    );
  };
  
  const renderVerificationStep = () => {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Zona de Servicio
          </Typography>
          
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500, color: '#333' }}>
            Zonas en las que ofreces servicios
          </Typography>
          <TextField
            fullWidth
            name="serviceAreas"
            value={formData.serviceAreas}
            onChange={handleChange}
            variant="outlined"
            placeholder="Ej: Colonia Condesa, Roma Norte, Polanco"
            sx={enhancedInputStyles}
          />
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500, color: '#333' }}>
            Radio de servicio (km)
          </Typography>
          <TextField
            fullWidth
            name="workRadius"
            type="number"
            value={formData.workRadius}
            onChange={handleChange}
            variant="outlined"
            placeholder="Ej: 10"
            sx={enhancedInputStyles}
          />
        </Grid>
        
        <Grid item xs={12}>
          <Divider sx={{ my: 3 }} />
          
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Términos y Condiciones
          </Typography>
          
          <Paper elevation={0} sx={{ p: 2, bgcolor: '#f9f9f9', borderRadius: '8px', mb: 2 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Al convertirte en un proveedor de servicios en Fixmigo, aceptas nuestros términos y condiciones, 
              incluyendo el código de conducta, políticas de pago, y compromisos de calidad de servicio. 
              Para más detalles, consulta nuestros Términos y Condiciones completos.
            </Typography>
          </Paper>
          
          <FormControlLabel
            control={
              <Checkbox 
                checked={formData.termsAccepted}
                onChange={handleChange}
                name="termsAccepted"
                color="primary"
                sx={{
                  color: '#FFD700',
                  '&.Mui-checked': {
                    color: '#FFD700',
                  },
                }}
              />
            }
            label="Acepto los términos y condiciones"
            sx={{ mb: 1 }}
          />
          
          {errorMessage && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errorMessage}
            </Alert>
          )}
        </Grid>
      </Grid>
    );
  };
  
  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return renderPersonalInfoStep();
      case 1:
        return renderProfessionalDetailsStep();
      case 2:
        return renderVerificationStep();
      default:
        return null;
    }
  };
  
  return (
    <Container maxWidth="md" sx={{ py: 5 }}>
      <Paper elevation={0} sx={{ p: 4, borderRadius: '16px', border: '1px solid #e0e0e0' }}>
        <Typography variant="h4" align="center" gutterBottom sx={{ color: '#2C3E50', fontWeight: 700 }}>
          Conviértete en Proveedor
        </Typography>
        
        <Typography variant="body1" align="center" sx={{ mb: 4, color: '#666' }}>
          Completa el siguiente formulario para aplicar como proveedor de servicios en Fixmigo. Una vez revisada tu solicitud, te contactaremos para continuar con el proceso.
        </Typography>
        
        {successMessage ? (
          <Alert severity="success" sx={{ my: 4 }}>
            {successMessage}
          </Alert>
        ) : (
          <form onSubmit={handleSubmit}>
            <Stepper 
              activeStep={activeStep} 
              alternativeLabel 
              connector={<ColorlibConnector />}
              sx={{ 
                width: '100%', 
                overflowX: 'auto', 
                mb: 5,
                '& .MuiStepLabel-label': {
                  color: theme => activeStep === steps.indexOf(steps[steps.indexOf(steps[activeStep])]) ? '#333' : '#999',
                  fontWeight: theme => activeStep === steps.indexOf(steps[steps.indexOf(steps[activeStep])]) ? 600 : 400,
                  fontSize: '0.95rem',
                  mt: 1
                },
                '& .MuiStepLabel-label.Mui-completed': {
                  color: '#333',
                  fontWeight: 500
                },
                '& .MuiSvgIcon-root.Mui-active': {
                  color: '#FFD700'
                },
                '& .MuiSvgIcon-root.Mui-completed': {
                  color: '#FFD700'
                }
              }}
            >
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
            
            {renderStepContent(activeStep)}
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 5 }}>
                {activeStep !== 0 && (
                  <Button 
                    onClick={handleBack}
                    sx={{
                      color: '#666',
                      borderColor: '#ccc',
                      '&:hover': {
                        borderColor: '#999',
                        backgroundColor: '#f5f5f5'
                      }
                    }}
                    variant="outlined"
                  >
                    Atrás
                  </Button>
                )}
                <Box sx={{ flex: '1 1 auto' }} />
                <Button
                  type={activeStep === steps.length - 1 ? 'submit' : 'button'}
                  onClick={activeStep === steps.length - 1 ? undefined : handleNext}
                  variant="contained"
                  sx={{
                    bgcolor: '#FFD700',
                    color: '#333',
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: '#F4CA16'
                    }
                  }}
                >
                  {activeStep === steps.length - 1 ? 'Enviar solicitud' : 'Siguiente'}
                </Button>
              </Box>
          </form>
        )}
      </Paper>
    </Container>
  );
};

export default ProviderApplication;

